// Layer: presentation. One stay, told from both sides.
//
// This view exists because every other screen shows the machinery: queues, predicates,
// a hash chain. None of them answers what the product was like for the person
// staying in the room. The left column is that answer; the right is why it happened.
//
// The empty left cell is load-bearing, not an omission. A queued action means a human
// has not acted yet, so the guest experienced nothing, and a timeline that quietly
// filled that space would be telling the operator a flattering story about their own
// service. Rendering the gap is the honest thing and the whole reason for two columns.
import { useCallback, useEffect, useState } from "react";
import type { StoriaApi } from "../application/ports";
import type { GuestJourney, GuestSummary, JourneyMoment } from "../domain/types";
import { ErrorPanel, messageOf } from "./ShiftView";

interface Props {
  api: StoriaApi;
}

export function GuestJourneyView({ api }: Props) {
  const [guests, setGuests] = useState<GuestSummary[] | null>(null);
  const [selected, setSelected] = useState<string>("");
  const [journey, setJourney] = useState<GuestJourney | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadGuests = useCallback(() => {
    setError(null);
    api
      .loadGuests()
      .then((found) => {
        setGuests(found);
        setSelected((current) => current || (found[0]?.id ?? ""));
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [api]);

  useEffect(loadGuests, [loadGuests]);

  useEffect(() => {
    if (!selected) return;
    setError(null);
    setJourney(null);
    api
      .loadGuestJourney(selected)
      .then(setJourney)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [api, selected]);

  // The landmark is rendered in every state, including loading and error. Returning a
  // bare paragraph early removes the <main> a screen reader navigates by, and on the
  // landing tab that means the page briefly has no main region at all.
  return (
    <main aria-label="Guest journey" className="stack">
      <header className="panel-head">
        <h2>Guest journey</h2>
        <p className="muted">What the guest experienced, and what caused it</p>
      </header>

      {error ? (
        <ErrorPanel message={error} onRetry={loadGuests} />
      ) : !guests ? (
        <p className="muted" role="status">Loading guests…</p>
      ) : guests.length === 0 ? (
        <p className="muted">
          No guests yet. They appear here once a booking arrives.
        </p>
      ) : (
        <>
          <div className="card">
            <label htmlFor="guest-picker">Guest</label>
            <select
              id="guest-picker"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              {guests.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.display_name}
                </option>
              ))}
            </select>
          </div>

          {!journey ? (
            <p className="muted" role="status">Loading journey…</p>
          ) : (
            <Timeline journey={journey} />
          )}
        </>
      )}
    </main>
  );
}

function Timeline({ journey }: { journey: GuestJourney }) {
  if (journey.moments.length === 0) {
    return (
      <p className="muted">
        Nothing has happened for {journey.display_name} yet.
      </p>
    );
  }
  return (
    <div className="journey">
      <div className="journey-head" aria-hidden="true">
        <span>What {journey.display_name} experienced</span>
        <span>What STORIA did</span>
      </div>
      <ol className="journey-list">
        {journey.moments.map((m) => (
          <Row key={m.sequence} moment={m} />
        ))}
      </ol>
    </div>
  );
}

function Row({ moment }: { moment: JourneyMoment }) {
  const when = new Date(moment.occurred_at);
  return (
    <li className="journey-row">
      <div className="journey-guest">
        <time dateTime={moment.occurred_at} className="mono muted">
          {when.toLocaleString()}
        </time>
        {moment.guest_side ? (
          <p className="journey-felt">{moment.guest_side}</p>
        ) : (
          <p className="journey-nothing">
            {moment.awaiting_human
              ? "Nothing yet, waiting on a person"
              : "Nothing the guest could notice"}
          </p>
        )}
      </div>
      <div className="journey-system">
        <span className={`badge badge-${moment.origin}`}>{moment.origin}</span>{" "}
        <span className="mono">{moment.kind}</span>
        {moment.status ? (
          <span className="badge badge-status"> {moment.status}</span>
        ) : null}
        <ul className="why">
          {moment.system_side.map((line) => (
            <li key={line} className="mono">
              {line}
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}
